import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { renderLinkifiedSegments, splitLinkified } from '../ui/LinkifiedText';

// Renders report text as Markdown: headings (# to ######), paragraphs,
// bullet / numbered lists (with indent), block quotes, fenced code, tables,
// horizontal rules, and inline **bold**, *italic*, ~~strike~~, `code` and
// [links](https://…). It builds React elements only - never HTML - so text can
// not inject markup, and a link must be http(s), mailto or a site path. Plain
// URLs and @mentions in running text still get the same highlighting an
// ordinary message gets (LinkifiedText's splitLinkified).
//
// "## " headings keep their show/hide behaviour: `onToggleSection` (owner only)
// adds a checkbox beside each one; `hiddenSections` are the headings currently
// off. Both live on the backend, so this component holds no state. Without
// `onToggleSection` the text renders as plain Markdown.

// No lookbehind: older Safari rejects it at parse time and would break the app.
const INLINE_RE = /(`[^`\n]+`)|\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|__(.+?)__|~~(.+?)~~|\*(?![\s*])([^*\n]+?)\*/g;

export function safeHref(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u) || /^mailto:/i.test(u)) return u;
  if (u.startsWith('/') && !u.startsWith('//')) return u;
  return null;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  const plain = (s: string) => {
    if (s) out.push(<Fragment key={`${keyPrefix}-p${n++}`}>{renderLinkifiedSegments(splitLinkified(s), `${keyPrefix}-p${n}`)}</Fragment>);
  };
  for (const m of text.matchAll(INLINE_RE)) {
    plain(text.slice(last, m.index));
    last = (m.index ?? 0) + m[0].length;
    const key = `${keyPrefix}-m${n++}`;
    if (m[1]) {
      out.push(
        <code key={key} className="rounded bg-black/[0.06] dark:bg-white/10 px-1 py-0.5 text-[0.92em] font-mono">
          {m[1].slice(1, -1)}
        </code>
      );
    } else if (m[2] !== undefined) {
      const href = safeHref(m[3]);
      out.push(
        href ? (
          <a
            key={key}
            href={href}
            target={href.startsWith('/') ? undefined : '_blank'}
            rel="noopener noreferrer nofollow"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 dark:text-blue-400 underline"
          >
            {renderInline(m[2], key)}
          </a>
        ) : (
          <Fragment key={key}>{m[2]}</Fragment>
        )
      );
    } else if (m[4] !== undefined || m[5] !== undefined) {
      out.push(<strong key={key}>{renderInline(m[4] ?? m[5], key)}</strong>);
    } else if (m[6] !== undefined) {
      out.push(<del key={key}>{renderInline(m[6], key)}</del>);
    } else {
      out.push(<em key={key}>{renderInline(m[7], key)}</em>);
    }
  }
  plain(text.slice(last));
  return out;
}

const HEADING_RE = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const BULLET_RE = /^(\s*)[-*+]\s+(.*)$/;
const ORDERED_RE = /^(\s*)(\d{1,3})[.)]\s+(.*)$/;
const RULE_RE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const splitRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

const HEADING_CLS = ['text-[1.15em] font-bold', 'font-semibold', 'font-semibold', 'font-semibold', 'font-medium', 'font-medium'];

export function MarkdownReportText({
  text,
  className,
  hiddenSections = [],
  onToggleSection,
}: {
  text: string;
  className?: string;
  hiddenSections?: string[];
  onToggleSection?: (nextHidden: string[]) => void;
}) {
  const hidden = hiddenSections;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const canToggle = !!onToggleSection && lines.some((l) => l.startsWith('## '));
  const blocks: ReactNode[] = [];
  let hiding = false;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const key = `b${i}`;

    const h = HEADING_RE.exec(line);
    if (h) {
      const level = h[1].length;
      const heading = h[2];
      if (level === 2 && canToggle) {
        const isHidden = hidden.includes(heading);
        hiding = isHidden;
        blocks.push(
          <label key={key} className="flex items-start gap-2 font-semibold mt-2 first:mt-0 cursor-pointer">
            <input
              type="checkbox"
              checked={!isHidden}
              onChange={() => onToggleSection?.(isHidden ? hidden.filter((x) => x !== heading) : [...hidden, heading])}
              aria-label={`Show section: ${heading}`}
              className="mt-1.5 shrink-0"
            />
            <span className={isHidden ? 'text-black/40 dark:text-white/40' : undefined}>{renderInline(heading, key)}</span>
          </label>
        );
        i++;
        continue;
      }
      if (level === 2) hiding = false;
      if (!hiding) {
        blocks.push(
          <p key={key} role="heading" aria-level={level} className={`${HEADING_CLS[level - 1]} mt-2 first:mt-0`}>
            {renderInline(heading, key)}
          </p>
        );
      }
      i++;
      continue;
    }
    if (hiding) {
      i++;
      continue;
    }

    // Fenced code block.
    if (/^\s*```/.test(line)) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i++]);
      i++; // closing fence
      blocks.push(
        <pre key={key} className="my-1 overflow-x-auto rounded-lg bg-black/[0.05] dark:bg-white/[0.07] p-2.5 text-[0.9em] font-mono whitespace-pre">
          {body.join('\n')}
        </pre>
      );
      continue;
    }

    // Table: a header row followed by a separator row.
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const head = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') rows.push(splitRow(lines[i++]));
      blocks.push(
        <div key={key} className="my-1 overflow-x-auto">
          <table className="border-collapse text-[0.95em]">
            <thead>
              <tr>
                {head.map((c, ci) => (
                  <th key={ci} className="border-b border-black/15 dark:border-white/20 px-2 py-1 text-left font-semibold whitespace-nowrap">
                    {renderInline(c, `${key}h${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {head.map((_, ci) => (
                    <td key={ci} className="border-b border-black/[0.07] dark:border-white/10 px-2 py-1 align-top">
                      {renderInline(r[ci] ?? '', `${key}r${ri}c${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (RULE_RE.test(line)) {
      blocks.push(<hr key={key} className="my-2 border-black/15 dark:border-white/20" />);
      i++;
      continue;
    }

    // Block quote: consecutive "> " lines.
    if (/^\s{0,3}>/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s{0,3}>/.test(lines[i])) body.push(lines[i++].replace(/^\s{0,3}>\s?/, ''));
      blocks.push(
        <blockquote key={key} className="my-1 border-l-2 border-black/20 dark:border-white/25 pl-3 text-black/70 dark:text-white/70">
          {body.map((b, bi) => (b.trim() === '' ? <div key={bi} className="h-1.5" /> : <p key={bi}>{renderInline(b, `${key}q${bi}`)}</p>))}
        </blockquote>
      );
      continue;
    }

    // Lists (bullet or numbered), indent = nesting depth.
    const li = BULLET_RE.exec(line) ?? ORDERED_RE.exec(line);
    if (li) {
      const items: ReactNode[] = [];
      while (i < lines.length) {
        const b = BULLET_RE.exec(lines[i]);
        const o = b ? null : ORDERED_RE.exec(lines[i]);
        if (!b && !o) break;
        const indent = (b ? b[1] : o![1]).replace(/\t/g, '  ').length;
        const body = b ? b[2] : o![3];
        items.push(
          <div key={`${key}l${i}`} className="flex items-start gap-1.5" style={{ paddingLeft: Math.min(indent, 12) * 6 }}>
            <span className="shrink-0 min-w-[1ch] text-black/60 dark:text-white/60">{b ? '•' : `${o![2]}.`}</span>
            <span>{renderInline(body, `${key}l${i}`)}</span>
          </div>
        );
        i++;
      }
      blocks.push(
        <div key={key} className="flex flex-col gap-0.5">
          {items}
        </div>
      );
      continue;
    }

    if (line.trim() === '') {
      blocks.push(<div key={key} className="h-1.5" />);
      i++;
      continue;
    }

    // Paragraph: this line plus following plain lines, kept as written (a report's
    // line breaks are meaningful).
    blocks.push(<p key={key}>{renderInline(line, key)}</p>);
    i++;
  }
  return <div className={className}>{blocks}</div>;
}

// A report in a list: at most `lines` lines tall (the rest is cut with a fade and
// a "…" cue; the full text is one click away in the details sheet). Block
// content - lists, tables, code - makes CSS line-clamp unusable, so this caps
// the height in lines (line-height is 1.5em) and checks whether anything is cut.
export function ClampedMarkdown({ text, lines = 10, className }: { text: string; lines?: number; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const el = box.current;
    if (el) setCut(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines]);
  return (
    <div className="relative">
      <div ref={box} className="overflow-hidden leading-[1.5]" style={{ maxHeight: `${lines * 1.5}em` }}>
        <MarkdownReportText text={text} className={className} />
      </div>
      {cut && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 flex h-[1.5em] items-end justify-start bg-gradient-to-t from-white via-white/90 to-transparent dark:from-[#10141a] dark:via-[#10141a]/90 text-black/45 dark:text-white/45"
        >
          …
        </div>
      )}
    </div>
  );
}
